import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { ContextMenuItem, ContextMenuSeparator } from "../../base/context-menu/context-menu";
import { isLaneColorHex, SWIMLANE_COLOR_PALETTE } from "./core/laneColorTones";
/** Accept `RRGGBB` without the leading `#` by prepending it. */ function normalizeHexInput(value) {
    const trimmed = value.trim();
    return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}
/**
 * Body of the swimlane color submenu. Swatches are plain buttons — not
 * `ContextMenuItem`s — so a click applies the color without closing the menu
 * and the user gets a live preview while trying shades.
 */ export function SwimlaneColorSubmenuContent({ laneKey, currentColor, onSetSwimlaneColor }) {
    const { t } = useTranslation();
    const [hexDraft, setHexDraft] = useState("");
    const normalizedDraft = normalizeHexInput(hexDraft);
    const draftValid = isLaneColorHex(normalizedDraft);
    const applyDraft = ()=>{
        if (draftValid) onSetSwimlaneColor(laneKey, normalizedDraft);
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "grid grid-cols-9 gap-1 p-1",
                children: SWIMLANE_COLOR_PALETTE.map((hex)=>{
                    const isSelected = hex.toLowerCase() === currentColor?.toLowerCase();
                    return /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "aria-label": hex,
                        style: {
                            background: hex
                        },
                        className: `h-5 w-5 rounded border border-black/10 ${isSelected ? "ring-2 ring-info-solid ring-offset-1" : ""}`,
                        onClick: ()=>onSetSwimlaneColor(laneKey, hex)
                    }, hex);
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-1 p-1",
                // Radix menu typeahead steals keystrokes typed into the input; stop
                // them from bubbling so free hex entry works — but let Escape through,
                // or the dismiss layer never sees it and the menu can't be closed
                // from the keyboard.
                onKeyDown: (e)=>{
                    if (e.key !== "Escape") e.stopPropagation();
                },
                children: [
                    /*#__PURE__*/ _jsx("input", {
                        type: "text",
                        value: hexDraft,
                        placeholder: t("processFlowEditor.swimlane.customColorPlaceholder"),
                        className: "h-7 w-full min-w-0 rounded border border-input bg-transparent px-2 text-xs",
                        onChange: (e)=>setHexDraft(e.target.value),
                        onKeyDown: (e)=>{
                            if (e.key === "Enter") applyDraft();
                        }
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        disabled: !draftValid,
                        className: "h-7 shrink-0 rounded border border-input px-2 text-xs disabled:opacity-50",
                        onClick: applyDraft,
                        children: t("processFlowEditor.swimlane.applyColor")
                    })
                ]
            }),
            currentColor && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(ContextMenuSeparator, {}),
                    /*#__PURE__*/ _jsxs(ContextMenuItem, {
                        onClick: ()=>onSetSwimlaneColor(laneKey, null),
                        children: [
                            /*#__PURE__*/ _jsx(RotateCcw, {
                                className: "mr-2 h-4 w-4"
                            }),
                            t("processFlowEditor.swimlane.resetColor")
                        ]
                    })
                ]
            })
        ]
    });
}

//# sourceMappingURL=SwimlaneColorSubmenu.js.map